import { parseNumber, round } from 'intl-utils';
import { onPageLoad } from 'runtime';
import { fetch, getName } from 'utils';

onPageLoad(import.meta, () => {
    const container = document.getElementById('paypal-express-container');
    const sessionData = JSON.parse(container.dataset.sessionData);
    const apiUrl = container.dataset.apiUrl ?? '/api/orders';
    const checkoutUrl = container.dataset.checkoutUrl ?? '/checkout';
    const updateEventName = container.dataset.updateEventName ?? 'cart:update';
    let paymentSummary = container.$data.paymentSummary ?? container.$data.cart.paymentSummary; // Falling back to the cart.paymentSummary saves us from defining a paymentSummary property on the buy now or cart views (which may have already been overridden).
    let deliveryAddressCountryCode = container.$data.deliveryAddress?.countryCode;
    let deliveryAddressPostcode = container.$data.deliveryAddress?.postcode;

    paypal.Buttons({
        style: {
            height: 44,
            disableMaxWidth: true
        },
        createOrder: async (data, actions) => {
            // Make sure the terms checkbox is checked (if applicable).
            const expressCheckoutTerms = document.querySelector('[name="ExpressCheckoutTerms"]');

            if (expressCheckoutTerms?.checked === false) {
                expressCheckoutTerms.classList.add('is-invalid');
                expressCheckoutTerms.focus();
                return;
            } else
                expressCheckoutTerms?.classList.remove('is-invalid');

            // Initially tried dispatching an event wrapped in a promise and resolving it within the event listener but then you must make sure the event listener is defined which isn't always the case.
            if (typeof window.initExpressCheckout === 'function' && await window.initExpressCheckout(container) === false)
                return;

            // Make sure a default delivery method is selected. If there is a delivery method id set then make sure it is in the available delivery methods.
            if (paymentSummary.isDeliveryRequired === true && (paymentSummary.deliveryMethodId == null || !paymentSummary.deliveryMethods.some(m => m.id == paymentSummary.deliveryMethodId)) && paymentSummary.deliveryMethods.length > 0)
                await setDeliveryMethod(paymentSummary.deliveryMethods[0].id);

            const shippingTotal = paymentSummary.lines
                .filter(line => line.type == 'delivery')
                .reduce((total, line) => total + parseNumber(line.subtotal) + parseNumber(line.subtotalTax), 0);

            return actions.order.create({
                intent: !sessionData['IsDeferred'] ? 'CAPTURE' : 'AUTHORIZE',
                purchase_units: [{
                    amount: {
                        currency_code: sessionData['Currency'],
                        value: parseNumber(paymentSummary.grandTotal),
                        breakdown: {
                            item_total: {
                                currency_code: sessionData['Currency'],
                                value: round(parseNumber(paymentSummary.grandTotal) - shippingTotal, 2) // Note: We must call round since in JavaScript you get small imprecisions when performing arithmetic. For example: console.log(0.2 + 0.1); // Output: 0.30000000000000004.
                            },
                            shipping: {
                                currency_code: sessionData['Currency'],
                                value: round(shippingTotal, 2)
                            }
                        }
                    },
                    shipping: paymentSummary.isDeliveryRequired === true ? {
                        options: paymentSummary.deliveryMethods
                            // We have an issue where when we have onShippingOptionsChange enabled it doesn't update the amount on PayPal. Commenting it out fixes it, however, we don't get access to the select delivery method in the onApprove event so we can't update it our end. For now we will just use the selected delivery methods and not allow the user to change it on PayPal. In the future we look at fixing this by doing a server post to PayPal to update the amount in the onShippingAddressChange (tried doing a JavaScript patch but this doesn't work).
                            .filter(deliveryMethod => deliveryMethod.isSelected)
                            .map(deliveryMethod => ({
                                id: deliveryMethod.id.toString(),
                                label: deliveryMethod.displayName ?? deliveryMethod.name,
                                type: deliveryMethod.isAddressRequired ? 'SHIPPING' : 'PICKUP',
                                selected: deliveryMethod.isSelected,
                                amount: {
                                    currency_code: sessionData['Currency'],
                                    value: round(parseNumber(deliveryMethod.amount) + parseNumber(deliveryMethod.amountTax), 2)
                                }
                            }))
                    } : null
                }],
                application_context: paymentSummary.isDeliveryRequired !== true ? {
                    shipping_preference: 'NO_SHIPPING'
                } : null
            });
        },
        onShippingAddressChange: async (data, actions) => {
            // Set the delivery address (you must do this before validating).
            deliveryAddressCountryCode = data.shippingAddress.countryCode;
            deliveryAddressPostcode = data.shippingAddress.postalCode;

            // Make sure the delivery method is valid for this address.
            if (!(await isDeliveryMethodValid(paymentSummary.deliveryMethodId)))
                return actions.reject(data.errors.METHOD_UNAVAILABLE);
        },
        /*onShippingOptionsChange: async (data, actions) => {
            // Make sure the delivery method is valid.
            if (!(await isDeliveryMethodValid(data.selectedShippingOption.id)))
                return actions.reject(data.errors.METHOD_UNAVAILABLE);

            // Set the delivery method.
            await setDeliveryMethod(data.selectedShippingOption.id);
        },*/
        onApprove: async (data, actions) => {
            let details;

            if (!sessionData['IsDeferred'])
                details = await actions.order.capture();
            else
                details = await actions.order.authorize();

            const formData = new FormData();
            formData.append('Contact.Name.FirstName', details.payer.name.given_name);
            formData.append('Contact.Name.LastName', details.payer.name.surname);
            formData.append('Contact.Email', details.payer.email_address);
            formData.append('IsDeliveryRequired', paymentSummary.isDeliveryRequired === true);
            formData.append('Delivery.DeliveryMethodId', paymentSummary.deliveryMethodId ?? '');

            if (paymentSummary.isDeliveryAddressRequired === true) {
                const shipping = details.purchase_units[0].shipping;
                const deliveryName = getName(shipping.name.full_name);
                const deliveryCountryResponse = await fetch(`/api/countries/${shipping.address.country_code}`);
                const deliveryCountryId = (await deliveryCountryResponse.json()).id;

                formData.append('Delivery.IsNewDeliveryAddressRequired', true);
                formData.append('Delivery.NewDeliveryAddress.Name.FirstName', deliveryName.firstName);
                formData.append('Delivery.NewDeliveryAddress.Name.LastName', deliveryName.lastName ?? '');
                formData.append('Delivery.NewDeliveryAddress.AddressLine1', shipping.address.address_line_1);
                formData.append('Delivery.NewDeliveryAddress.AddressLine2', shipping.address.address_line_2 ?? '');
                formData.append('Delivery.NewDeliveryAddress.Town', shipping.address.admin_area_2);
                formData.append('Delivery.NewDeliveryAddress.County', shipping.address.admin_area_1 ?? '');
                formData.append('Delivery.NewDeliveryAddress.CountryId', deliveryCountryId);
                formData.append('Delivery.NewDeliveryAddress.Postcode', shipping.address.postal_code);
            }

            formData.append('Payment.PaymentMethodId', container.dataset.paymentMethodId);
            formData.append('Payment.Token', data.orderID);
            formData.append('Terms', true);

            // Create the order.
            await fetch(`${checkoutUrl}?${paymentSummary.sessionId != null ? `sessionId=${encodeURIComponent(paymentSummary.sessionId)}&` : ''}isBillingAddressRequired=false&isCardDetailsRequired=false`, {
                method: 'POST',
                body: formData
            });

            // Confirm the order. This will update the processor reference number with the capture/authorize id.
            const response = await fetch(`${apiUrl}/${data.orderID}/execute`, {
                method: 'POST'
            });

            const queryString = paymentSummary.sessionId != null ? `?sessionId=${encodeURIComponent(paymentSummary.sessionId) }` : '';
            window.location.href = response.ok ? `${checkoutUrl}/complete${queryString}` : `${checkoutUrl}/failed${queryString}`;
        },
        onError: error => {
            console.warn(error);
        }
    }).render('#paypal-express-button');

    document.addEventListener(updateEventName, e => {
        // Update the payment summary.
        paymentSummary = e.detail.paymentSummary;
    });

    async function isDeliveryMethodValid(id) {
        const sessionId = paymentSummary.sessionId;
        const response = await fetch(`/api/cart/validate-delivery-method?id=${id}&sessionId=${sessionId != null ? encodeURIComponent(sessionId) : ''}&deliveryAddressCountryCode=${deliveryAddressCountryCode ? encodeURIComponent(deliveryAddressCountryCode) : ''}&deliveryAddressPostcode=${deliveryAddressPostcode ? encodeURIComponent(deliveryAddressPostcode) : ''}`, {
            method: 'POST'
        });

        return response.ok;
    }

    async function setDeliveryMethod(id) {
        const sessionId = paymentSummary.sessionId;
        const response = await fetch(`/api/cart/set-delivery-method?id=${id}&sessionId=${sessionId != null ? encodeURIComponent(sessionId) : ''}&deliveryAddressCountryCode=${deliveryAddressCountryCode ? encodeURIComponent(deliveryAddressCountryCode) : ''}&deliveryAddressPostcode=${deliveryAddressPostcode ? encodeURIComponent(deliveryAddressPostcode) : ''}`, {
            method: 'POST'
        });
        const data = await response.json();

        container.dispatchEvent(new CustomEvent(updateEventName, { bubbles: true, detail: data }));
    }
});